import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SignaturePad } from 'angular2-signaturepad';
import { defaultTo } from 'lodash-es';

import {
  FieldKinds,
  FormFieldValidatorFactory,
  FormSignatureService,
  SignatureFieldActions,
  User,
} from '@site-mate/dashpivot-shared-library';

import { FormBaseComponent } from 'app/form/form-components/form-base.component';
import { FormService } from 'app/form/form.service';
import { FieldWeb } from 'app/shared/model/item.model';
import { AppUtilService } from 'app/shared/service/app-util.service';
import { TeamService } from 'app/shared/service/team.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-form-signature',
  templateUrl: 'form-signature.component.html',
  styleUrls: [
    'form-signature.component.scss',
    '../../../form/form-components/form-component.scss',
    '../../../shared/component/signature-pad.component.scss',
  ],
})
export class FormSignatureComponent extends FormBaseComponent implements OnInit {
  @Input() formId: string;
  @Input() formPath: string;
  @Input() instanceName;
  @Input() items;
  @Input() model: FieldWeb;
  @Input() isPreview: boolean;
  @Input() formCreator: User;

  @Output() handleNormalSign = new EventEmitter();
  @Output() handleApprovalSign = new EventEmitter();
  @Output() handleManualSign = new EventEmitter();
  @Output() reset = new EventEmitter();

  signaturePad: SignaturePad;
  canSignApprovalSignature = false;
  canResetSignature = false;
  isApprovalSignatureRestricted = false;

  public sharedSignatureService = new FormSignatureService();
  restrictedBannerTextApproval =
    'You do not have the correct user type or permission to sign. Please contact your workspace admin if you are required to sign this field.';
  restrictedBannerTextReset =
    'You do not have the correct user type or permission to reset this workflow. Please contact your workspace admin if you are required to reset this workflow.';

  constructor(
    protected readonly appUtilService: AppUtilService,
    protected readonly formService: FormService,
    protected readonly toastrService: ToastrService,
    private readonly userService: UserService,
    private readonly teamService: TeamService,
  ) {
    super(appUtilService, formService);
  }

  ngOnInit() {
    this.validator = FormFieldValidatorFactory.getValidator(this.model);

    if (this.isApprovalSignature) {
      this.setApprovalSignaturePermissions();
    }

    this.setResetSignaturePermissions();
    this.isApprovalSignatureRestricted =
      !this.model.signatureUrl &&
      !this.canSignApprovalSignature &&
      !this.notFirstUnSigned() &&
      Boolean(this.formId);
  }

  private setApprovalSignaturePermissions() {
    this.canSignApprovalSignature = this.sharedSignatureService.canPerformFieldAction({
      user: this.userService.getCurrentUser(),
      teamPath: this.teamService.getCurrentTeam()?.path,
      fieldAction: SignatureFieldActions.ApprovalSignature,
      rules: this.model.signatureRules || [],
      formCreatorId: this.formCreator?._id,
    });
  }

  private setResetSignaturePermissions() {
    this.canResetSignature = this.sharedSignatureService.canPerformFieldAction({
      user: this.userService.getCurrentUser(),
      teamPath: this.teamService.getCurrentTeam()?.path,
      fieldAction: SignatureFieldActions.ResetWorkflow,
      rules: this.model.signatureRules || [],
      formCreatorId: this.formCreator?._id,
    });
  }

  notFirstUnSigned() {
    const allUnSigned = this.items.filter((i) => {
      return i.kind === FieldKinds.Signature && !i.signatureUrl;
    });
    if (allUnSigned && allUnSigned.length > 0) {
      return this.model.id !== allUnSigned[0].id;
    }

    return false;
  }

  lastSigned() {
    const allSigned = this.items.filter((i) => {
      return i.kind === FieldKinds.Signature && i.signatureUrl;
    });
    if (allSigned && allSigned.length > 0) {
      return this.model.id === allSigned[allSigned.length - 1].id;
    }

    return false;
  }

  onPreviewSign() {
    this.toastrService.warnByKey('formPreviewWarningSignature');
  }

  approvalSign() {
    if (this.formId) {
      this.onFieldChange();
      this.handleApprovalSign.emit({ id: defaultTo(this.model.id, this.model._id) });
    }
  }

  sign() {
    if (this.formId) {
      this.onFieldChange();
      this.handleNormalSign.emit(this.model);
    }
  }

  get isApprovalSignature() {
    return this.model.kind === FieldKinds.Signature;
  }

  get isManualSignature() {
    return this.model.isManualSignature;
  }

  get isNormalSignature() {
    return this.model.kind === FieldKinds.SignatureArray && !this.isManualSignature;
  }

  addManualSignature() {
    if (this.formId) {
      this.onFieldChange();
      this.handleManualSign.emit(this.model);
    }
  }

  resetSignature() {
    this.onFieldChange();
    this.reset.emit();
  }
}
